import { useRef, useEffect } from 'react'
import { useTheme } from '../../contexts/ThemeContext'
import Logo from './Logo'
import NavigationMenu from './NavigationMenu'
import ThemeToggle from './ThemeToggle'
import AuthButtons from './AuthButtons'
import { guestNavigationItems } from '../../config/navigation'
import 'primeicons/primeicons.css'

interface MobileMenuProps {
  isOpen: boolean
  onClose: () => void
}

export default function MobileMenu({ isOpen, onClose }: MobileMenuProps) {
  const { theme } = useTheme()
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className={`lg:hidden fixed inset-0 backdrop-blur-sm z-40 ${
          theme === 'dark' ? 'bg-black/50' : 'bg-gray-900/50'
        }`}
        onClick={onClose}
      />

      {/* Menu */}
      <nav
        ref={menuRef}
        className={`lg:hidden fixed top-0 left-0 right-0 border-b-2 border-gold-500 shadow-xl z-50 ${
          theme === 'dark' ? 'bg-black' : 'bg-gray-200'
        }`}
      >
        <div className="container mx-auto px-4">
          {/* Header with Logo and Close Button */}
          <div className="flex items-center justify-between h-16 md:h-20 py-2 mb-4">
            <Logo onClick={onClose} />
            <button
              onClick={onClose}
              className={`w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-300 active:scale-95 ${
                theme === 'dark'
                  ? 'text-gold-400 hover:text-gold-300 hover:bg-gold-900/20'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gold-400/20'
              }`}
              aria-label="Close menu"
            >
              <span className="text-2xl font-bold">×</span>
            </button>
          </div>

          <NavigationMenu
            items={guestNavigationItems}
            variant="mobile"
            onLinkClick={onClose}
          />

          <ThemeToggle variant="mobile" />
          <AuthButtons variant="mobile" onLinkClick={onClose} />
        </div>
      </nav>
    </>
  )
}

